import { useState, useMemo } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { useToggleProvider } from '@/hooks/useAdmin'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { adminApiService, AdminRateLookup, AdminPricingRate, AdminPhoneNumber } from '@/services/adminApi'
import { COUNTRIES_CONFIG } from '@/config/countries'
import { getCountryName } from '@/lib/countries'
import { Flag } from '@/components/Flag'
import { Globe, Clock, Route as RouteIcon, Search, Settings, Play, Phone, Check, X, ChevronsUpDown, ChevronUp, ChevronDown } from 'lucide-react'

// Radix color palette available in our CSS — order chosen for visual contrast between neighbors
const RADIX_COLORS = ['blue', 'amber', 'jade', 'red', 'indigo', 'green'] as const

const typeColorCache = new Map<string, { bg: string; text: string }>()

function typeColor(type: string): { bg: string; text: string } {
  let entry = typeColorCache.get(type)
  if (!entry) {
    const color = RADIX_COLORS[typeColorCache.size % RADIX_COLORS.length]
    entry = { bg: `var(--${color}-3)`, text: `var(--${color}-11)` }
    typeColorCache.set(type, entry)
  }
  return entry
}

function OriginNumberSelect({
  value,
  onChange,
  ownedNumbers,
}: {
  value: string
  onChange: (v: string) => void
  ownedNumbers: AdminPhoneNumber[]
}) {
  return (
    <div className="flex items-center gap-2">
      <Phone className="h-4 w-4 text-muted-foreground" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
      >
        <option value="">No origin filter</option>
        {ownedNumbers.map((n) => (
          <option key={n.number} value={n.number}>
            {n.number} ({n.type})
          </option>
        ))}
      </select>
      {value && (
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={() => onChange('')}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  )
}

type RateSortKey = 'country' | 'type' | 'provider' | 'cost' | 'price' | 'margin'
type SortDir = 'asc' | 'desc'

function SortIcon({ column, sortKey, sortDir }: { column: RateSortKey; sortKey: RateSortKey; sortDir: SortDir }) {
  if (column !== sortKey) return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-40" />
  return sortDir === 'asc'
    ? <ChevronUp className="ml-1 inline h-3 w-3" />
    : <ChevronDown className="ml-1 inline h-3 w-3" />
}

function sortRates(rates: AdminPricingRate[], key: RateSortKey, dir: SortDir): AdminPricingRate[] {
  const mul = dir === 'asc' ? 1 : -1
  return [...rates].sort((a, b) => {
    let cmp = 0
    if (key === 'country') {
      const nameA = getCountryName(a.isoCountry) ?? a.isoCountry
      const nameB = getCountryName(b.isoCountry) ?? b.isoCountry
      cmp = nameA.localeCompare(nameB)
    } else if (key === 'type') {
      cmp = a.inferredType.localeCompare(b.inferredType)
    } else if (key === 'provider') {
      cmp = (a.provider ?? '').localeCompare(b.provider ?? '')
    } else if (key === 'cost') {
      cmp = a.costPerMin - b.costPerMin
    } else if (key === 'price') {
      cmp = a.pricePerMin - b.pricePerMin
    } else if (key === 'margin') {
      const mA = a.pricePerMin > 0 ? (a.pricePerMin - a.costPerMin) / a.pricePerMin : 0
      const mB = b.pricePerMin > 0 ? (b.pricePerMin - b.costPerMin) / b.pricePerMin : 0
      cmp = mA - mB
    }
    return cmp * mul
  })
}

function ActiveRatesTable({
  rates,
  originNumber,
  onOriginChange,
  ownedNumbers,
}: {
  rates: AdminPricingRate[]
  originNumber: string
  onOriginChange: (v: string) => void
  ownedNumbers: AdminPhoneNumber[]
}) {
  const [countryFilter, setCountryFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [surchargeOnly, setSurchargeOnly] = useState(false)
  const [sortKey, setSortKey] = useState<RateSortKey>('country')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const hasOrigin = !!originNumber

  function handleSort(key: RateSortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const filtered = useMemo(() => {
    const f = rates.filter((r) => {
      if (countryFilter) {
        const searchable = `${r.isoCountry} ${getCountryName(r.isoCountry)}`.toLowerCase()
        if (!searchable.includes(countryFilter.toLowerCase())) return false
      }
      if (typeFilter && r.inferredType !== typeFilter) return false
      if (surchargeOnly && !r.isSurcharge) return false
      return true
    })
    return sortRates(f, sortKey, sortDir)
  }, [rates, countryFilter, typeFilter, surchargeOnly, sortKey, sortDir])

  const types = useMemo(() => {
    return [...new Set(rates.map((r) => r.inferredType))].sort()
  }, [rates])

  const sortProps = (key: RateSortKey, extraClass?: string) => ({
    className: ['cursor-pointer select-none hover:text-foreground', extraClass].filter(Boolean).join(' '),
    onClick: () => handleSort(key),
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="max-w-48">
          <Input
            placeholder="Filter country..."
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant={typeFilter === '' ? 'default' : 'outline'}
            onClick={() => setTypeFilter('')}
            className="h-8 text-xs"
          >
            All
          </Button>
          {types.map((t) => (
            <Button
              key={t}
              size="sm"
              variant={typeFilter === t ? 'default' : 'outline'}
              onClick={() => setTypeFilter(t)}
              className="h-8 text-xs"
            >
              {t}
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          variant={surchargeOnly ? 'default' : 'outline'}
          onClick={() => setSurchargeOnly((v) => !v)}
          className="h-8 text-xs"
        >
          Surcharge
        </Button>
        <OriginNumberSelect
          value={originNumber}
          onChange={onOriginChange}
          ownedNumbers={ownedNumbers}
        />
        <span className="ml-auto self-center text-xs text-muted-foreground">
          {filtered.length} of {rates.length} routes
        </span>
      </div>
      <div className="max-h-[600px] overflow-y-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead {...sortProps('country')}>
                Country<SortIcon column="country" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('type')}>
                Type<SortIcon column="type" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('provider')}>
                Provider<SortIcon column="provider" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead>Prefixes</TableHead>
              <TableHead>Origin</TableHead>
              {hasOrigin && <TableHead>Eligible</TableHead>}
              <TableHead {...sortProps('cost', 'text-right')}>
                Cost<SortIcon column="cost" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('price', 'text-right')}>
                Our Price<SortIcon column="price" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
              <TableHead {...sortProps('margin', 'text-right')}>
                Margin<SortIcon column="margin" sortKey={sortKey} sortDir={sortDir} />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((rate, i) => {
              const country = COUNTRIES_CONFIG[rate.isoCountry]
              const margin = rate.pricePerMin > 0 && rate.costPerMin > 0
                ? ((rate.pricePerMin - rate.costPerMin) / rate.pricePerMin * 100).toFixed(0)
                : null
              return (
                <TableRow
                  key={i}
                  className={
                    hasOrigin
                      ? rate.eligible
                        ? 'bg-green-50 dark:bg-green-950/20'
                        : 'opacity-40'
                      : undefined
                  }
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {country && <Flag countryCode={country.code} size="sm" />}
                      {getCountryName(rate.isoCountry)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const colors = typeColor(rate.inferredType)
                      return (
                        <Badge
                          variant="outline"
                          className="text-xs border-transparent"
                          style={{ backgroundColor: colors.bg, color: colors.text }}
                        >
                          {rate.inferredType}
                        </Badge>
                      )
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={rate.provider === 'telnyx' ? 'outline' : 'secondary'}
                      className="text-xs capitalize"
                    >
                      {rate.provider ?? 'twilio'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-48 truncate" title={rate.destPrefixes.join(', ')}>
                    {rate.destPrefixes.slice(0, 5).join(', ')}
                    {rate.destPrefixes.length > 5 && ` +${rate.destPrefixes.length - 5}`}
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-32 truncate" title={rate.origPrefixes.join(', ')}>
                    {rate.origPrefixes.slice(0, 3).join(', ')}
                    {rate.origPrefixes.length > 3 && ` +${rate.origPrefixes.length - 3}`}
                  </TableCell>
                  {hasOrigin && (
                    <TableCell>
                      {rate.eligible ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right font-mono text-xs">
                    {rate.costPerMin}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs font-medium">
                    <span className={rate.isSurcharge ? 'text-amber-600 dark:text-amber-400' : ''}>
                      {rate.pricePerMin}
                    </span>
                    {rate.isSurcharge && (
                      <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0 border-amber-400 text-amber-600 dark:text-amber-400">
                        surcharge
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    {margin ? `${margin}%` : '-'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function ProviderStatusCard() {
  const { data } = useQuery(adminQueries.providers())
  const toggleProvider = useToggleProvider()
  const providers = data?.providers ?? []
  const anyDisabled = providers.some((p) => !p.enabled)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Provider Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-6">
          {providers.map((p) => (
            <div key={p.name} className="flex items-center gap-3">
              <Switch
                checked={p.enabled}
                onCheckedChange={() => toggleProvider.mutate(p.name)}
                disabled={toggleProvider.isPending}
              />
              <Badge
                variant={p.enabled ? 'default' : 'destructive'}
                className="capitalize"
              >
                {p.name}
              </Badge>
              <span className="text-sm text-muted-foreground">{p.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          ))}
        </div>
        {anyDisabled && (
          <p className="text-sm text-amber-600">
            Disabled providers will be skipped during call routing. Re-enable to restore.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export const Route = createFileRoute('/admin/pricing/')({
  component: AdminPricingPage,
})

function AdminPricingPage() {
  const { data, isLoading } = useQuery(adminQueries.pricing())
  const [originNumber, setOriginNumber] = useState('')
  const { data: phoneData } = useQuery(adminQueries.phoneNumbers())
  const ownedNumbers = phoneData?.phoneNumbers ?? []

  const { data: activeRates, isLoading: ratesLoading } = useQuery(adminQueries.pricingVersionRates(
    data?.activeVersionId ?? null,
    originNumber || undefined
  ))

  const [lookupNumber, setLookupNumber] = useState('')
  const [lookupResult, setLookupResult] = useState<AdminRateLookup | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [lookupLoading, setLookupLoading] = useState(false)

  const handleLookup = async () => {
    if (!lookupNumber.trim()) return
    setLookupLoading(true)
    setLookupError(null)
    setLookupResult(null)
    try {
      const result = await adminApiService.lookupRate(lookupNumber.trim(), originNumber || undefined)
      setLookupResult(result)
    } catch (err: any) {
      setLookupError(err.message || 'Lookup failed')
    } finally {
      setLookupLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold">Pricing</h1>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const generatedDate = data?.generatedAt
    ? new Date(data.generatedAt * 1000)
    : null

  const country = lookupResult ? COUNTRIES_CONFIG[lookupResult.isoCountry] : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Pricing</h1>
        <div className="flex items-center gap-3">
          {generatedDate && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Last updated: {generatedDate.toLocaleString()}
            </div>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/pricing/generate">
              <Play className="mr-1 h-4 w-4" />
              Generate
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/pricing/versions">
              <Settings className="mr-1 h-4 w-4" />
              Versions
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card className="py-4 gap-3">
          <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Countries</CardTitle>
            <div className="rounded-md bg-blue-500/10 p-1.5 shrink-0">
              <Globe className="h-3.5 w-3.5 text-blue-500" />
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-0.5">
            <p className="text-2xl font-bold tracking-tight">{data?.totalCountries ?? 0}</p>
            <p className="text-xs text-muted-foreground">with active rates</p>
          </CardContent>
        </Card>
        <Card className="py-4 gap-3">
          <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Routes</CardTitle>
            <div className="rounded-md bg-green-500/10 p-1.5 shrink-0">
              <RouteIcon className="h-3.5 w-3.5 text-green-500" />
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-0.5">
            <p className="text-2xl font-bold tracking-tight">{data?.totalRoutes ?? 0}</p>
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{(data?.totalPrefixes ?? 0).toLocaleString()}</span> prefixes
            </p>
          </CardContent>
        </Card>
        <Card className="py-4 gap-3 col-span-2">
          <CardHeader className="flex flex-row items-center justify-between px-4 py-0">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Route Types</CardTitle>
            <div className="rounded-md bg-violet-500/10 p-1.5 shrink-0">
              <RouteIcon className="h-3.5 w-3.5 text-violet-500" />
            </div>
          </CardHeader>
          <CardContent className="px-4">
            <div className="flex flex-wrap gap-2">
              {data?.typeCounts &&
                Object.entries(data.typeCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => {
                    const colors = typeColor(type)
                    return (
                      <div key={type} className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className="text-xs border-transparent"
                          style={{ backgroundColor: colors.bg, color: colors.text }}
                        >
                          {type}
                        </Badge>
                        <span className="text-sm font-bold tracking-tight">{count}</span>
                      </div>
                    )
                  })}
            </div>
          </CardContent>
        </Card>
      </div>

      <ProviderStatusCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="h-5 w-5" />
            Rate Lookup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 max-w-sm space-y-2">
              <Label htmlFor="lookup-number">Destination Number</Label>
              <Input
                id="lookup-number"
                placeholder="e.g. +447596441974"
                value={lookupNumber}
                onChange={(e) => setLookupNumber(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
              />
            </div>
            <OriginNumberSelect
              value={originNumber}
              onChange={setOriginNumber}
              ownedNumbers={ownedNumbers}
            />
            <Button onClick={handleLookup} disabled={lookupLoading || !lookupNumber.trim()}>
              {lookupLoading ? 'Looking up...' : 'Lookup'}
            </Button>
          </div>

          {lookupError && (
            <p className="text-sm text-destructive">{lookupError}</p>
          )}

          {lookupResult && (
            <div className="rounded-lg border p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Country</p>
                  <div className="flex items-center gap-2 font-medium">
                    {country && <Flag countryCode={country.code} size="sm" />}
                    {getCountryName(lookupResult.isoCountry)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  {(() => {
                    const colors = typeColor(lookupResult.inferredType)
                    return (
                      <Badge
                        variant="outline"
                        className="border-transparent"
                        style={{ backgroundColor: colors.bg, color: colors.text }}
                      >
                        {lookupResult.inferredType}
                      </Badge>
                    )
                  })()}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Our Price</p>
                  <p className="text-xl font-bold">
                    {lookupResult.currency} {lookupResult.pricePerMin}/min
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Price (cents)</p>
                  <p className="font-medium">{lookupResult.pricePerMinCents}p/min</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Route</p>
                <p className="text-sm">{lookupResult.friendlyName}</p>
              </div>
              {lookupResult.routes?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Available Routes</p>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Provider</TableHead>
                          <TableHead>Origin Prefix</TableHead>
                          {originNumber && <TableHead>Status</TableHead>}
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lookupResult.routes.map((route, i) => {
                          const margin = route.pricePerMin > 0 && route.costPerMin > 0
                            ? ((route.pricePerMin - route.costPerMin) / route.pricePerMin * 100).toFixed(0)
                            : null
                          return (
                            <TableRow
                              key={i}
                              className={
                                originNumber
                                  ? route.chosen
                                    ? 'bg-green-50 dark:bg-green-950/20'
                                    : route.eligible
                                      ? ''
                                      : 'opacity-40'
                                  : undefined
                              }
                            >
                              <TableCell>
                                <Badge
                                  variant={route.provider === 'telnyx' ? 'outline' : 'secondary'}
                                  className="text-xs capitalize"
                                >
                                  {route.provider}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs max-w-32 truncate" title={route.origPrefixes.join(', ')}>
                                {route.origPrefixes.slice(0, 3).join(', ')}
                                {route.origPrefixes.length > 3 && ` +${route.origPrefixes.length - 3}`}
                              </TableCell>
                              {originNumber && (
                                <TableCell>
                                  {route.chosen ? (
                                    <Badge variant="default" className="text-xs">Chosen</Badge>
                                  ) : route.eligible ? (
                                    <Badge variant="secondary" className="text-xs">Eligible</Badge>
                                  ) : (
                                    <X className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </TableCell>
                              )}
                              <TableCell className="text-right font-mono text-xs">
                                {lookupResult.currency} {route.costPerMin}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs font-medium">
                                {lookupResult.currency} {route.pricePerMin}
                              </TableCell>
                              <TableCell className="text-right text-xs">
                                {margin ? `${margin}%` : '-'}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active Rates</CardTitle>
        </CardHeader>
        <CardContent>
          {ratesLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : !activeRates?.length ? (
            <p className="text-sm text-muted-foreground">
              No active pricing version.{' '}
              <Link to="/admin/pricing/versions" className="underline">Upload and activate one</Link>.
            </p>
          ) : (
            <ActiveRatesTable
              rates={activeRates}
              originNumber={originNumber}
              onOriginChange={setOriginNumber}
              ownedNumbers={ownedNumbers}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
